import { Link } from 'react-router-dom'
import { Trophy, Medal, Award, Users } from 'lucide-react'

interface LeaderboardPlayer {
  id: string
  display_name: string
  total_games: number
  wins: number
  individual_points: number
  avg_points_per_game: number
  win_rate: number
}

interface LeaderboardProps {
  players: LeaderboardPlayer[]
  title?: string
  maxItems?: number
}

export function Leaderboard({ players, title = "Leaderboard", maxItems = 10 }: LeaderboardProps) {
  const displayPlayers = players.slice(0, maxItems)

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Trophy className="h-5 w-5 text-yellow-500" />
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />
      case 2:
        return <Award className="h-5 w-5 text-amber-600" />
      default:
        return <span className="text-sm font-medium text-gray-500 w-5 text-center">{index + 1}</span>
    }
  }

  const getRankColor = (index: number) => {
    switch (index) {
      case 0:
        return 'bg-yellow-50 border-yellow-200'
      case 1:
        return 'bg-gray-50 border-gray-200'
      case 2:
        return 'bg-amber-50 border-amber-200'
      default:
        return 'bg-white border-gray-200'
    }
  }

  if (displayPlayers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2" />
          {title}
        </h3>
        <div className="text-center text-gray-500 py-8">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No games played yet</p>
          <p className="text-sm">Start recording games to see the leaderboard!</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <Users className="h-5 w-5 mr-2" />
        {title}
      </h3>
      <div className="space-y-2">
        {displayPlayers.map((player, index) => (
          <div
            key={player.id}
            className={`flex items-center justify-between p-3 rounded-lg border ${getRankColor(index)}`}
          >
            <div className="flex items-center space-x-3 flex-1">
              {getRankIcon(index)}
              <div className="flex-1">
                <Link 
                  to={`/players/${player.id}`}
                  className="font-medium text-gray-900 hover:text-blue-600 hover:underline transition-colors"
                >
                  {player.display_name}
                </Link>
                <p className="text-sm text-gray-500">{player.total_games} games</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500 uppercase">Win Rate</p>
                <p className="font-semibold text-gray-900">{player.win_rate}%</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Wins</p>
                <p className="font-semibold text-gray-900">{player.wins}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Avg Pts</p>
                <p className="font-semibold text-gray-900">{player.avg_points_per_game}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

